/**
 * @fileoverview Dammen Header Component
 * @description Displays turn indicator, player info, and captured pieces
 */

import React, { memo } from "react";
import { View, Text, StyleSheet } from "react-native";
import { Image } from "expo-image";
import { useThemeColors } from "@/providers/ThemeProvider";
import { PlayerId, CHARACTER_COLORS } from "../types";

interface PlayerInfo {
  id: string;
  name: string;
  avatar_url?: string | null;
}

interface DammenHeaderProps {
  currentTurn: PlayerId;
  myPiece: PlayerId | null;
  isMyTurn: boolean;
  opponentInfo: PlayerInfo | null;
  myName: string;
  capturedPieces: { opa: number; oma: number };
  isAIGame?: boolean;
  aiDifficulty?: string;
}

/**
 * Character SVG images
 */
const CHARACTER_IMAGES: Record<PlayerId, any> = {
  opa: require("@/assets/games/opa_endless_runner.svg"),
  oma: require("@/assets/games/omam_endless_runner.svg"),
};

/**
 * Dammen Header Component
 */
export const DammenHeader: React.FC<DammenHeaderProps> = memo(
  ({
    currentTurn,
    myPiece,
    isMyTurn,
    opponentInfo,
    myName,
    capturedPieces,
    isAIGame = false,
    aiDifficulty,
  }) => {
    const colors = useThemeColors();

    const opponentPiece = myPiece === "opa" ? "oma" : "opa";
    const opponentName = isAIGame
      ? `Computer (${aiDifficulty === "easy" ? "Makkelijk" : aiDifficulty === "hard" ? "Moeilijk" : "Gemiddeld"})`
      : opponentInfo?.name || "Tegenstander";

    return (
      <View style={[styles.container, { backgroundColor: colors.surface }]}>
        {/* Opponent side */}
        <View style={styles.playerSection}>
          <View
            style={[
              styles.playerBadge,
              {
                backgroundColor: CHARACTER_COLORS[opponentPiece],
                opacity: currentTurn === opponentPiece ? 1 : 0.5,
              },
            ]}
          >
            <Image
              source={CHARACTER_IMAGES[opponentPiece]}
              style={styles.characterIcon}
              contentFit="contain"
            />
          </View>
          <View style={styles.playerInfo}>
            <Text
              style={[
                styles.playerName,
                { color: colors.text },
                currentTurn === opponentPiece && styles.activeTurn,
              ]}
              numberOfLines={1}
            >
              {opponentName}
            </Text>
            <Text style={[styles.capturedText, { color: colors.textSecondary }]}>
              Geslagen: {opponentPiece === "opa" ? capturedPieces.opa : capturedPieces.oma}
            </Text>
          </View>
          {currentTurn === opponentPiece && (
            <View style={[styles.turnIndicator, { backgroundColor: colors.primary }]}>
              <Text style={styles.turnText}>⏳</Text>
            </View>
          )}
        </View>

        {/* Divider with VS */}
        <View style={styles.divider}>
          <Text style={[styles.vsText, { color: colors.textSecondary }]}>VS</Text>
        </View>

        {/* My side */}
        <View style={styles.playerSection}>
          {currentTurn === myPiece && (
            <View style={[styles.turnIndicator, { backgroundColor: colors.success }]}>
              <Text style={styles.turnText}>✓</Text>
            </View>
          )}
          <View style={[styles.playerInfo, { alignItems: "flex-end" }]}>
            <Text
              style={[
                styles.playerName,
                { color: colors.text },
                currentTurn === myPiece && styles.activeTurn,
              ]}
              numberOfLines={1}
            >
              {myName} (Jij)
            </Text>
            <Text style={[styles.capturedText, { color: colors.textSecondary }]}>
              Geslagen: {myPiece === "opa" ? capturedPieces.opa : capturedPieces.oma}
            </Text>
          </View>
          <View
            style={[
              styles.playerBadge,
              {
                backgroundColor: myPiece ? CHARACTER_COLORS[myPiece] : colors.border,
                opacity: currentTurn === myPiece ? 1 : 0.5,
              },
            ]}
          >
            {myPiece && (
              <Image
                source={CHARACTER_IMAGES[myPiece]}
                style={styles.characterIcon}
                contentFit="contain"
              />
            )}
          </View>
        </View>

        {/* Turn message */}
        <View style={[styles.turnMessage, { backgroundColor: isMyTurn ? colors.success : colors.warning }]}>
          <Text style={styles.turnMessageText}>
            {isMyTurn ? "Jouw beurt!" : "Tegenstander aan zet..."}
          </Text>
        </View>
      </View>
    );
  }
);

DammenHeader.displayName = "DammenHeader";

const styles = StyleSheet.create({
  container: {
    padding: 12,
    borderRadius: 12,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  playerSection: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  playerBadge: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
  },
  characterIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  playerInfo: {
    flex: 1,
    marginHorizontal: 12,
  },
  playerName: {
    fontSize: 16,
    fontWeight: "600",
  },
  activeTurn: {
    fontWeight: "700",
  },
  capturedText: {
    fontSize: 12,
    marginTop: 2,
  },
  turnIndicator: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  turnText: {
    fontSize: 14,
  },
  divider: {
    alignItems: "center",
    marginVertical: 4,
  },
  vsText: {
    fontSize: 12,
    fontWeight: "600",
  },
  turnMessage: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 8,
  },
  turnMessageText: {
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: 14,
  },
});

